import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  BookOpen, 
  CheckCircle, 
  PenLine,
  TrendingUp,
  Activity
} from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      // Only show error toast for genuine failures, not auth issues
      if (error.response?.status !== 401) {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 px-4 max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded-lg w-1/3" />
            <div className="grid md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <section className="mb-8 animate-fade-in" data-testid="admin-header">
          <h1 className="text-3xl md:text-4xl text-foreground mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Overview of TrueNorth Zone platform.</p>
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 animate-fade-in" style={{ animationDelay: "100ms" }}>
          <Card className="glass-card stat-card" data-testid="stat-members">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="stat-icon w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {stats?.total_members || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total Members</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card stat-card" data-testid="stat-active">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="stat-icon w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-400" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {stats?.active_members || 0}
              </div>
              <div className="text-sm text-muted-foreground">Active (7 days)</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card stat-card" data-testid="stat-completions">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="stat-icon w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {stats?.lesson_completions || 0}
              </div>
              <div className="text-sm text-muted-foreground">Lessons Completed</div>
            </CardContent>
          </Card>
          
          <Card className="glass-card stat-card" data-testid="stat-journals">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="stat-icon w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <PenLine className="w-5 h-5 text-purple-400" />
                </div>
              </div>
              <div className="text-3xl font-heading text-foreground mb-1">
                {stats?.journal_entries || 0}
              </div>
              <div className="text-sm text-muted-foreground">Journal Entries</div>
            </CardContent>
          </Card>
        </section>

        {/* Course Stats */}
        <section className="mb-12 animate-fade-in" style={{ animationDelay: "200ms" }}>
          <h2 className="text-xl font-heading text-foreground mb-4">Quest Performance</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {stats?.course_stats?.map((course, index) => (
              <Card key={course.course_id} className="glass-card stat-card" data-testid={`course-stat-${index}`}>
                <CardContent className="p-6">
                  <h3 className="text-foreground font-medium mb-4">{course.title}</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Enrolled</span>
                      <span className="text-foreground">{course.enrolled_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="text-foreground">{course.completed_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completion Rate</span>
                      <span className="text-primary font-medium">{course.completion_rate}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="animate-fade-in" style={{ animationDelay: "300ms" }}>
          <h2 className="text-xl font-heading text-foreground mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-4 gap-4">
            <Card 
              className="glass-card card-hover cursor-pointer"
              onClick={() => navigate("/admin/members")}
              data-testid="quick-action-members"
            >
              <CardContent className="p-6 flex items-center gap-4">
                <Users className="w-6 h-6 text-primary" />
                <span className="text-foreground">Manage Members</span>
              </CardContent>
            </Card>
            <Card 
              className="glass-card card-hover cursor-pointer"
              onClick={() => navigate("/admin/courses")}
              data-testid="quick-action-courses"
            >
              <CardContent className="p-6 flex items-center gap-4">
                <BookOpen className="w-6 h-6 text-primary" />
                <span className="text-foreground">Manage Quests</span>
              </CardContent>
            </Card>
            <Card 
              className="glass-card card-hover cursor-pointer"
              onClick={() => navigate("/admin/analytics")}
              data-testid="quick-action-analytics"
            >
              <CardContent className="p-6 flex items-center gap-4">
                <TrendingUp className="w-6 h-6 text-primary" />
                <span className="text-foreground">View Analytics</span>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
}
